import Linkify from 'linkify-react';
import React, { useRef, useState } from 'react';
import { BsArrowDownCircle } from 'react-icons/bs';
import { IoCheckmarkCircle } from 'react-icons/io5';
import styled, { css } from 'styled-components';

import { useAppContext } from '../../appContext';
import CameraIcon from '../../assets/camera.svg';
import PostButton from '../../assets/post-button.svg';
import XCircle from '../../assets/x-circle.svg';
import useUserSession from '../../hooks/useUserSession';
import { useStateContext } from '../../store/stateContext';
import { User } from '../../types';
import GLogin from '../google-login';

// Styled components
const FormWrapper = styled.div`
    width: 100%;
    @media (max-width: 768px) {
      width: 100% !important;
      padding: 16px !important; /* 1rem = 16px */
    }
`;

const FormOverlay = styled.div`
  background-color: transparent !important; 
  z-index: 100 !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  box-sizing: border-box !important;
  overflow: visible !important;
`;

const FormContainer = styled.form<{ isPurchase: boolean; }>`
  width: 100% !important;
  background-color: white !important;
  border-radius: 8px !important; /* 0.5rem = 8px */
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1) !important;
  border: none !important;
  overflow: auto !important;
  z-index: 101 !important;
  margin: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  position: relative !important;
  bottom: 0 !important;
  border: 2px solid ${props => props.isPurchase ? '#23C68D' : '#5942D1'} !important;
  @media (min-width: 768px) {
    width: 100% !important;
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px !important; /* 0.75rem = 12px */
  gap: 8px !important; /* 0.5rem = 8px */
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
`;

const CommentText = styled.p`
  all: revert;
  font-size: 14px;
  font-weight: 400;
  color: black;
  width: 83.333333%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled(BsArrowDownCircle) <{ isPurchase: boolean }>`
  cursor: pointer;
  border-radius: 9999px;
  font-size: 30px !important;
  color: white;
  background-color: ${props => props.isPurchase ? '#23C68D' : '#5942D1'};
  
  @media (min-width: 768px) {
    background-color: ${props => props.isPurchase ? '#5942D1' : '#5942D1'};
  }
`;

const StepTitle = styled.p<{ isPurchase: boolean }>`
  all: revert;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.isPurchase ? '#23C68D' : '#5942D1'} !important;
  margin: 0 !important;
  
  @media (min-width: 768px) {
    color: ${props => props.isPurchase ? '#5942D1' : '#5942D1'};
  }
`;

const FormGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px !important; /* 0.25rem = 4px */
`;

const InputLabel = styled.label<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px !important; /* 0.5rem = 8px */
  color: #888888 !important;
  border: none !important;
  padding: 8px !important; /* 0.5rem = 8px */
  padding-top: 12px !important; /* 0.75rem = 12px */
  padding-bottom: 12px !important; /* 0.75rem = 12px */
  border-radius: 8px !important; /* 0.5rem = 8px */
  background-color: #EEEEEE !important;
  
  &:focus-within {
    outline: none !important;
  }
  
  ${props => props.hasError && css`
    border-color: #DB5858 !important;
  `}
`;

const FormInput = styled.input`
  && {
    all:unset !important;
    line-height: 20px;
    border: 0px;
    border-radius: 0;
    color: #888888 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    flex-grow: 1;
    width: 100% !important;
    background-color: #EEEEEE !important;
    font-family: 'Sora', sans-serif !important;
    
    &:focus {
      outline: none;
    }
    &::placeholder {
      color: #888888 !important;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #DB5858;
  font-size: 10px;
`;

const FileInputLabel = styled.label<{ hasFileName: boolean }>`
  color: ${props => props.hasFileName ? 'black' : '#999999'} !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  border: none !important;
  padding: 8px !important; /* 0.5rem = 8px */
  padding-top: 12px !important; /* 0.75rem = 12px */
  padding-bottom: 12px !important; /* 0.75rem = 12px */
  border-radius: 8px !important; /* 0.5rem = 8px */
  background-color: #EEEEEE !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  
  &:focus {
    outline: none !important;
  }
`;

const FileNameText = styled.span`
  cursor: pointer;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  width: 100%;
`;

const HiddenFileInput = styled.input`
  line-height: 20px;
  display: none !important;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: black !important;
  font-size: 12px !important;
  &::before,
  &::after {
    content: '';
    flex: 1 !important;
    height: 1px !important;
    background-color: #e5e7eb !important;
    margin: 0 16px !important; /* 1rem = 16px */
  }
`;

const SubmitButton = styled.button<{ isPurchase: boolean; isSubmitting: boolean }>`
  width: 100% !important;
  padding: 12px !important; /* 0.75rem = 12px */
  background-color: #5942D1 !important;
  color: white !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border: none !important;
  border-radius: 0 !important;
  cursor: ${props => props.isSubmitting ? 'not-allowed' : 'pointer'} !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  height: 48px !important;
  
  &:hover:not(:disabled) {
    background-color: #4935c0 !important;
  }
`;

const ButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const LoadingDotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  span {
    width: 10px;
    height: 10px;
    margin: 0 5px;
    background-color: #d1c6f2;
    border-radius: 50%;
    animation: bounce 1.5s infinite;
    
    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.3s;
    }
    &:nth-child(3) {
      animation-delay: 0.6s;
    }
  }
`;

const StyledLink = styled.a<{ isDarkMode: boolean }>`
  text-decoration: underline;
  color: ${props => props.isDarkMode ? '#5942D1' : '#23C68D'};
  
  &:hover {
    background-color: ${props => props.isDarkMode ? '#f1f5f9' : 'transparent'} !important;
  }
`;

const ErrorIcon = styled.img`
  color: #DB5858;
  font-weight: bold;
  width: 24px !important;
  height: 24px !important;
`;

const SuccessIcon = styled(IoCheckmarkCircle)`
  color: #23C68D;
  font-weight: bold;
  width: 24px !important;
  height: 24px !important;
`;

const DisclaimerContainer = styled.p`
    all: revert;
    padding: 0 16px !important; /* 1rem = 16px */
    text-align: center !important;
    font-weight: 400 !important;
    color: #888888 !important;
    font-size: 9px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 4px !important; /* 0.25rem = 4px */
`;

const DisclaimerLink = styled.a`
    color: #888888;
`;

export const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 ]*$/
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// End Adornment component definition before the main component
const EndAdornment = ({ isError }: { isError: boolean }) => {
  return isError ? (
    <ErrorIcon src={XCircle} />
  ) : (
    <SuccessIcon />
  );
};

interface UserFormProps {
  onSuccess: (user: Partial<User>, additionalPayload: Record<string, unknown> | undefined) => void;
  onClose: () => void;
  additionalPayload?: Record<string, unknown>;
  commentText?: string
  label?: string;
}

const UserForm: React.FC<UserFormProps> = ({ onSuccess, onClose, commentText, label = 'Post my reply', additionalPayload }) => {
  const { login } = useUserSession();
  const [formValues, setFormValues] = useState<Record<string, string | File | null | undefined>>({ fullName: '', email: '', profilePicture: null });
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({ fullName: undefined, email: undefined });
  const [fileName, setFileName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLDivElement>(null);
  const { isPurchase } = useAppContext();
  const { state } = useStateContext();
  const isDarkMode = state.themeMode === 'dark';

  const validate = () => {
    const errors: { fullName?: string; email?: string } = {};
    if (!formValues.fullName || formValues.fullName.toString().trim() === '') {
      errors.fullName = 'Please enter a valid name';
    }
    if (!ALPHANUMERIC_REGEX.test(formValues.fullName as string)) {
      errors.fullName = 'Name should contain only alphanumeric characters';
    }

    if (!formValues.email) {
      errors.email = 'Please enter a valid email';
    } else if (!EMAIL_REGEX.test(formValues.email as string)) {
      errors.email = 'Please enter a valid email';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    if (name === 'profilePicture' && files && files.length > 0) {
      const file = files[0];
      setFileName(file.name);
      setFormValues({ ...formValues, profilePicture: file });
    } else {
      setFormValues({ ...formValues, [name]: value });
      setFormErrors({ ...formErrors, [name]: undefined });
    }
  };

  const validateFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const errors = { ...formErrors };
    const name = e.target.value;
    if (name.trim() === '') {
      errors.fullName = 'Please enter a valid name';
    } else {
      if (!ALPHANUMERIC_REGEX.test(name)) {
        errors.fullName = 'Name should contain only alphanumeric characters';
      } else {
        errors.fullName = name ? '' : undefined
      }
    }
    setFormErrors(errors);
  }

  const validateEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const errors = { ...formErrors };
    const email = e.target.value;
    if (email.trim() === '') {
      errors.email = 'Please enter a valid email';
    } else if (!EMAIL_REGEX.test(email)) {
      errors.email = 'Please enter a valid email';
    } else {
      errors.email = email ? '' : undefined
    }
    setFormErrors(errors);
  }

  const handleGoogleLoginSuccess = async ({ email, name, profilePictureUrl }: { email: string, name: string, profilePictureUrl?: string }) => {
    const updatedFormValues = { ...formValues, email, fullName: name, profilePictureUrl };
    setFormValues(updatedFormValues);
    setFormErrors({ ...formErrors, email: undefined, name: undefined });
    await login({ email: email, name: name });
    onSuccess(updatedFormValues, additionalPayload);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(async () => {
      if (validate()) {
        await login({ email: formValues.email as string, name: formValues.fullName as string });
        onSuccess(formValues, additionalPayload);
      }
      setIsSubmitting(false);
    }, 500)
  };

  const componentDecorator = (href: string, text: string, key: number) => (
    <StyledLink
      href={href}
      key={key}
      target="_blank"
      rel="noopener noreferrer"
      isDarkMode={isDarkMode}
    >
      {text.slice(0, 20)}...
    </StyledLink>
  );

  return (
    <FormWrapper ref={formRef} id='form-wrapper'>
      <FormOverlay >
        <FormContainer onSubmit={handleSubmit} isPurchase={!!isPurchase}>
          <FormContent>
            <HeaderContainer>
              <Linkify componentDecorator={componentDecorator}>
                <CommentText>{commentText}</CommentText>
              </Linkify>
              <CloseButton isPurchase={!!isPurchase} onClick={onClose} />
            </HeaderContainer>

            <StepTitle isPurchase={!!isPurchase}>
              Final step!
            </StepTitle>

            <FormGroup>
              <InputLabel hasError={!!formErrors.fullName}>
                <FormInput
                  name="fullName"
                  type="text"
                  placeholder="Full name"
                  value={formValues.fullName as string}
                  onChange={handleChange}
                  onBlur={validateFirstName}
                />
                {formErrors.fullName !== undefined &&
                  <EndAdornment isError={!!formErrors.fullName || (formValues?.fullName as string).trim() === ''} />
                }
              </InputLabel>
              {formErrors.fullName && <ErrorMessage>{formErrors.fullName}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <InputLabel hasError={!!formErrors.email}>
                <FormInput
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formValues.email as string}
                  onChange={handleChange}
                  onBlur={validateEmail}
                />
                {formErrors.email !== undefined &&
                  <EndAdornment isError={!!formErrors.email} />
                }
              </InputLabel>
              {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <HiddenFileInput
                name="profilePicture"
                type="file"
                id="profile-picture"
                accept='image/*'
                onChange={handleChange}
              />
              <FileInputLabel
                htmlFor="profile-picture"
                hasFileName={!!fileName}
              >
                <FileNameText>{fileName || 'Upload profile picture (optional)'}</FileNameText>
                {!fileName && <img src={CameraIcon} alt="camera" />}
              </FileInputLabel>
            </FormGroup>

            <Divider>or</Divider>
            <GLogin onSuccess={handleGoogleLoginSuccess} />
          </FormContent>
          <DisclaimerContainer>
            By commenting, you agree to our <DisclaimerLink href="https://userpop.me/privacy-policy/" target="_blank">Privacy Policy</DisclaimerLink>.
          </DisclaimerContainer>

          <SubmitButton
            disabled={isSubmitting}
            type="submit"
            isPurchase={!!isPurchase}
            isSubmitting={isSubmitting}
          >
            {isSubmitting ? (
              <LoadingDotsContainer>
                <span></span>
                <span></span>
                <span></span>
              </LoadingDotsContainer>
            ) : (
              <>
                {label || "Post my reply"} <ButtonIcon src={PostButton} />
              </>
            )}
          </SubmitButton>
        </FormContainer>
      </FormOverlay>
    </FormWrapper>
  );
};

export default UserForm;
